// The manifest — the one file to edit to add a sound.
//
// A sound is a file plus the four things the engine has to know before it can
// decide *not* to play it: which bus it belongs to, how loud it is before the
// board has its say, how close together two of them may be, and how many may
// sound at once. Those last two are the whole of what keeps a factory from
// buzzing: a turn moves an army, ticks every side's machines and lands a dozen
// ingots, so an engine that played everything it was told to would play forty
// files in a quarter of a second.
//
// Every number here is a starting value and expected to move in the playtest
// phase. None of them is load-bearing for correctness — a wrong cooldown is a
// sound that is too busy or too sparse, never a sound that throws.

/**
 * The four things a volume slider can be about.
 *
 * `sfx` is the board, `ui` is the chrome, and the two exist apart because a
 * player who wants a quiet board still wants to hear their own clicks. `music`
 * and `ambience` have nothing on them yet and are here so that adding one is a
 * row in this file rather than a fifth slider and a migration.
 */
export type Bus = "music" | "ambience" | "sfx" | "ui";

export type SoundId =
  | "artifact-claimed"
  | "artifact-placed"
  | "bishop"
  | "card-fan"
  | "card-play"
  | "craft"
  | "drag"
  | "king"
  | "knight"
  | "pawn"
  | "queen"
  | "rook"
  | "generate-board"
  | "refine"
  | "resources"
  | "step"
  | "take";

export interface SoundSpec {
  /** Under `public/sounds/`. */
  file: string;
  bus: Bus;
  /** Base gain, before the bus and before the board's distance falloff. */
  gain: number;
  /** The engine drops a replay of this id inside this window. */
  cooldownMs: number;
  /** Most instances of this id sounding at once; the rest are dropped. */
  voices: number;
  /**
   * Further files that are the *same* sound; each play picks one of them.
   *
   * Not a second id, because a take is not a second event: the cooldown and
   * the voice cap are about the gesture, and a fan sifted across four ids
   * would be four gestures that never thin each other.
   */
  takes?: readonly string[];
  /** Whether a caller is expected to hand it a `Mix`. */
  positional: boolean;
  /** ± playback-rate variance, so repeats do not read as one held note. */
  rateJitter?: number;
}

/**
 * Two sounds of the same thing closer together than this are one sound.
 *
 * Ninety milliseconds is under the gap at which two clicks stop being two
 * clicks, so the drop is inaudible; what it removes is the flam of a tick
 * reporting six of one event on the same frame.
 */
const COOLDOWN_MS = 90;

/**
 * The step's own window, longer because it fires most.
 *
 * The one id that can be asked for many times in a frame, and the one where a
 * dropped repeat costs nothing.
 */
const STEP_COOLDOWN_MS = 120;

/**
 * The fan's own window, shorter because sifting is one continuous gesture.
 *
 * A seat is 14px wide at the tightest pack, so a pointer crossing the hand
 * enters several inside a frame; seventy milliseconds is what thins that from
 * a burst of card slaps into a rustle, while still letting a deliberate hop
 * card to card sound every time.
 */
const FAN_COOLDOWN_MS = 70;

/** Enough for a busy square without the pile-up a whole board would make. */
const VOICES = 3;
/** For the ones that arrive in handfuls: a body stepping, a lane delivering. */
const VOICES_MANY = 4;

/**
 * The six pieces' own voices.
 *
 * One file per rank, and one shape for all six: they are heard at the same two
 * moments — the forge that minted one, and the click that selected one — so a
 * difference between rows would be a difference between pieces, which is the
 * file's job and not the manifest's. The forge plays them as they are; the
 * selection plays them through `SELECT_VOICE` (`src/client/ai/ui/soundMap.ts`), which is
 * where "less important" is decided.
 */
const PIECE_VOICE: Omit<SoundSpec, "file"> = {
  bus: "sfx",
  gain: 0.9,
  cooldownMs: COOLDOWN_MS,
  voices: VOICES,
  positional: true
};

export const SOUNDS: Record<SoundId, SoundSpec> = {
  // An artifact joining the hand, from wherever — a crown reaching the king,
  // a station's craft, the dev panel. Chrome rather than board: nothing on the
  // board did it, and it is not at a square. One voice, because it is a
  // fanfare and two fanfares at once is a mess.
  "artifact-claimed": {
    file: "artifact-claimed.mp3",
    bus: "ui",
    gain: 0.9,
    cooldownMs: COOLDOWN_MS,
    voices: 1,
    positional: false
  },
  // An artifact structure standing up from a token, and the same file under
  // a click on one: the pieces' arrangement exactly, one row and two weights.
  "artifact-placed": { file: "artifact-placed.mp3", ...PIECE_VOICE },
  bishop: { file: "bishop.mp3", ...PIECE_VOICE },
  king: { file: "king.mp3", ...PIECE_VOICE },
  knight: { file: "knight.mp3", ...PIECE_VOICE },
  pawn: { file: "pawn.mp3", ...PIECE_VOICE },
  queen: { file: "queen.mp3", ...PIECE_VOICE },
  rook: { file: "rook.mp3", ...PIECE_VOICE },
  // Sifting the hand. Four takes and a wide jitter on purpose: fanning is the
  // one gesture that fires this many times a second, and a card sound heard
  // eight times unchanged reads as one file being retriggered rather than as
  // cards moving under a hand.
  "card-fan": {
    file: "card-fan-1.mp3",
    takes: ["card-fan-2.mp3", "card-fan-3.mp3", "card-fan-4.mp3"],
    bus: "ui",
    gain: 0.6,
    cooldownMs: FAN_COOLDOWN_MS,
    voices: VOICES_MANY,
    rateJitter: 0.12,
    positional: false
  },
  // A card actually spent. Loud, and deliberately not jittered: it is an
  // occasion, and a pitch-shifted occasion reads as a different event.
  "card-play": {
    file: "card-play.mp3",
    bus: "ui",
    gain: 0.9,
    cooldownMs: COOLDOWN_MS,
    voices: 2,
    positional: false
  },
  // A forge minting something with no voice of its own — a monster. The six
  // ranks above speak for themselves.
  craft: {
    file: "craft.mp3",
    bus: "sfx",
    gain: 0.9,
    cooldownMs: COOLDOWN_MS,
    voices: VOICES,
    positional: true
  },
  // A piece crossing distance — a slide rather than a step. Not jittered: it
  // is long enough that a rate change is a pitch change rather than variety.
  drag: {
    file: "drag.mp3",
    bus: "sfx",
    gain: 0.7,
    cooldownMs: COOLDOWN_MS,
    voices: VOICES,
    positional: true
  },
  // The board arriving. Not positional: it is not at a square, it *is* the
  // squares, and a whole board panned to one ear would be plainly wrong.
  "generate-board": {
    file: "generate-board.mp3",
    bus: "sfx",
    gain: 0.8,
    cooldownMs: COOLDOWN_MS,
    voices: 1,
    positional: false
  },
  refine: {
    file: "refine.mp3",
    bus: "sfx",
    gain: 0.7,
    cooldownMs: COOLDOWN_MS,
    voices: VOICES,
    positional: true
  },
  // Ore arriving somewhere. Jittered, because a lane delivers several in a row
  // into the same square and identical repeats read as a buzz.
  resources: {
    file: "resources.mp3",
    bus: "sfx",
    gain: 0.6,
    cooldownMs: COOLDOWN_MS,
    voices: VOICES_MANY,
    rateJitter: 0.05,
    positional: true
  },
  // The commonest sound in the game, so the quietest and the most jittered.
  step: {
    file: "step.mp3",
    bus: "sfx",
    gain: 0.55,
    cooldownMs: STEP_COOLDOWN_MS,
    voices: VOICES_MANY,
    rateJitter: 0.06,
    positional: true
  },
  take: {
    file: "take.mp3",
    bus: "sfx",
    gain: 1,
    cooldownMs: COOLDOWN_MS,
    voices: VOICES,
    positional: true
  }
};

/**
 * Where a sound file lives.
 *
 * `BASE_URL` and never a bare `/`: a non-root deployment sets `VITE_BASE_PATH`,
 * and an absolute path would fetch the host's root instead. The same reading
 * `src/client/ai/render/models.ts` takes of the GLBs, which are served the same way.
 */
export const soundUrl = (file: string): string => `${import.meta.env.BASE_URL}sounds/${file}`;
